import {
    useBorrowerLoanType,
    useBorrowerLoanSubType,
    useBorrowerBuildRentTypeId,
} from '../../../queries/BorrowerFields/CoreFields';
import { useApplicationProperties } from '../../../queries/BorrowerFields/ApplicationFields';
import { DropDownBox } from '@apply/components';
import { ApplicationQuestionReviewItem } from '../../components/ApplicationQuestionReviewItem';
import { LoanType, LoanSubType, LoanTypeOptions, LoanSubTypeOptions, BuildRentTypeOptions } from '@apply/constants';
import { useAuthentication } from '../../../Login/hooks/useAuthentication';
import { useEffect, useState } from 'react';
import { ConfirmV2 } from '@apply/ui';
import { IApplicationProperties } from '@apply/api/src/api/ApplyApi';
import { v4 as uuidv4 } from 'uuid';
import { useAddPropertyToCache } from '../../Property/queries/useAddPropertyToCache';

interface LoanTypeProps {
    entryGuid: string | undefined;
    required?: boolean;
    isReview?: boolean;
    submitted?: boolean;
}

const LIQUIDITY_ACCESS_VALUE = 'liquidityAccess';

const activeSubTypesFor = (loanTypeId: string | number) =>
    LoanSubTypeOptions.filter((s) => s.loanType === loanTypeId && s.active == true).sort(
        ({ sortOrder: a }, { sortOrder: b }) => a - b
    );

/**
 * Loan types that have sub-types are offered as one dropdown row per sub-type, so a single
 * selection has to carry both halves. Encoded as `loanType:loanSubType`, with the sub-type
 * half left empty for the types that have none.
 */
const toDropDownValue = (loanType: LoanType | undefined, loanSubType: LoanSubType | undefined): string | undefined => {
    if (!loanType) return undefined;

    // An application saved before this loan type gained sub-types has no sub-type to match a row
    // with. Show nothing selected so the borrower is forced to pick one.
    if (activeSubTypesFor(loanType).length > 0 && !loanSubType) return undefined;

    return `${loanType}:${loanSubType ?? ''}`;
};

const fromDropDownValue = (value: string) => {
    const [loanTypePart, loanSubTypePart] = value.split(':');
    return {
        loanType: Number(loanTypePart) as LoanType,
        loanSubType: loanSubTypePart ? (Number(loanSubTypePart) as LoanSubType) : undefined,
    };
};

export const LoanTypeDropDown: React.FC<LoanTypeProps> = ({ entryGuid, required = true, isReview = false }) => {
    const { isLocEnabled } = useAuthentication();
    const [loanType, set_loanType] = useBorrowerLoanType();
    const [loanSubType, set_loanSubType] = useBorrowerLoanSubType();
    const [buildRentTypeId] = useBorrowerBuildRentTypeId();
    const [properties, set_properties] = useApplicationProperties();
    const addPropertyToCache = useAddPropertyToCache();

    const [openConfirmLoanTypeChange, set_openConfirmLoanTypeChange] = useState<boolean>(false);
    const [unConfirmedLoanType, set_unConfirmedLoanType] = useState<
        { loanType: LoanType; loanSubType: LoanSubType | undefined } | undefined
    >(undefined);
    const [isRendered, set_isRendered] = useState(false);

    const loanTypeLabel = (loanTypeId: string | number, loanTypeName: string) => {
        if (loanTypeId == LoanType.BuildRent) {
            return 'Build-Rent or Fix-Rent';
        }
        return loanTypeName;
    };

    const loanTypeOptions = [
        ...LoanTypeOptions.filter((t) => t.id && t.id !== LoanType.MasterNote && t.active == true)
            .sort(({ sortOrder: a }, { sortOrder: b }) => a - b)
            .flatMap((lt) => {
                const subTypes = activeSubTypesFor(lt.id);

                // Expanding in place keeps the sub-type rows in the parent type's sort position.
                if (subTypes.length > 0) {
                    return subTypes.map((s) => ({
                        value: `${lt.id}:${s.id}`,
                        label: s.name,
                        disabled: false,
                    }));
                }

                return [
                    {
                        value: `${lt.id}:`,
                        label: loanTypeLabel(lt.id, lt.name),
                        disabled:
                            (lt.id === LoanType.SubNoteConstruction && !isLocEnabled) ||
                            (lt.id === LoanType.SubNoteFixFlip && !isLocEnabled),
                    },
                ];
            }),
        { value: LIQUIDITY_ACCESS_VALUE, label: 'Liquidity Access (Coming Soon)', disabled: true },
    ];

    const getLoanType = () => {
        if (loanType) {
            if (loanType === LoanType.BuildRent) {
                return BuildRentTypeOptions.find((t) => t.id === buildRentTypeId)?.name;
            } else if (loanSubType) {
                return LoanSubTypeOptions.find((t) => t.id === loanSubType)?.name;
            } else {
                return LoanTypeOptions.find((t) => t.id === loanType)?.name;
            }
        } else {
            return 'Unknown';
        }
    };

    useEffect(() => {
        set_isRendered(true);
    }, []);

    const onChange = (value: string) => {
        const { loanType: nextLoanType, loanSubType: nextLoanSubType } = fromDropDownValue(value);
        if (!nextLoanType) return;

        const hasProperties = (properties?.items?.length ?? 0) > 0;

        // Only a change of the loan type itself invalidates property data. Switching between the
        // sub-types of one loan type keeps the same property form, so it applies silently.
        const isLoanTypeChange = !!loanType && loanType !== nextLoanType;

        if (isRendered && entryGuid != undefined && hasProperties && isLoanTypeChange) {
            set_unConfirmedLoanType({ loanType: nextLoanType, loanSubType: nextLoanSubType });
            set_openConfirmLoanTypeChange(true);
        } else {
            set_loanType(nextLoanType);
            set_loanSubType(nextLoanSubType);
        }
    };

    const onConfirmChange = () => {
        if (!unConfirmedLoanType) return;

        const newPropertyGuid = uuidv4();
        set_loanType(unConfirmedLoanType.loanType);
        set_loanSubType(unConfirmedLoanType.loanSubType);
        set_unConfirmedLoanType(undefined);
        set_openConfirmLoanTypeChange(false);
        set_properties({ isDirty: false, currentPropertyId: null, items: [], forms: [] } as IApplicationProperties);
        addPropertyToCache(newPropertyGuid);
    };

    const onCancelChange = () => {
        set_unConfirmedLoanType(undefined);
        set_openConfirmLoanTypeChange(false);
    };

    return (
        <>
            {isReview ? (
                <ApplicationQuestionReviewItem
                    label='Loan Type'
                    value={getLoanType()}
                />
            ) : (
                <>
                    <DropDownBox
                        id='loanType'
                        value={toDropDownValue(loanType, loanSubType)}
                        onChange={onChange}
                        label='Loan Type'
                        required={required}
                        dataSource={loanTypeOptions}
                        valueExpr='value'
                        displayExpr='label'
                    />
                    <ConfirmV2
                        id='openConfirmLoanTypeChangeModal'
                        title='Change Loan Type?'
                        okLabel='Continue'
                        cancelLabel='Cancel'
                        open={openConfirmLoanTypeChange}
                        onConfirm={onConfirmChange}
                        onCancel={onCancelChange}
                        height='250px'
                        width='400px'
                    >
                        <p>Changing the loan type will delete any property information.</p>
                        <p>Are you sure you want to continue?</p>
                    </ConfirmV2>
                </>
            )}
        </>
    );
};
